import { FormEvent, useState } from 'react'
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Modal, Select } from 'antd'
import { useApolloClient, useQuery } from '@apollo/client'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { useSelector } from 'react-redux'
import quoteDetailsForInviteAndMember from '@Quotes/components/QuoteShare/query'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import Link from '~/core/Link'
import { getUserIdentity } from '~/redux-modules/identity'
import { areAllRequiredFieldsFilled, hasErrors } from '~/lib/formHelpers'
import { isEmployeeEmailQuery } from '../SecureProjectsHomePage/query'

import { getValidDescription, getValidFunctionality } from './logic'
import {
    RELEASE_DFM_CONFIRMATION,
    RELEASE_DFM_TITLE,
    RELEASE_QUOTE_TITLE,
    EMAIL_SUBJECT_PLACEHOLDER,
    TO,
    CC,
    SUBJECT,
    FROM,
    NOTES,
    REQUIRED_FIELDS,
} from './constants'

interface ConfirmationModalPropsType {
    onClose: () => void
    quoteId: string | null
    orderId?: string
    isReleaseDfm?: boolean
    isReleaseDfmOdp?: boolean
    onSubmit?: () => void
    setIsReleasing?: Function
    form: WrappedFormUtils
}

const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 19 },
}

const customizeEmailFormStyles = {
    marginBottom: 0,
    alignItems: 'center',
    marginLeft: '20px',
}

const customizeEmailFormTextStyles = {
    fontSize: '14px',
    color: getColor('blackAlpha85'),
}

const { Option } = Select

function ReleaseDFMConfirmationModal(props: ConfirmationModalPropsType) {
    const {
        onClose,
        quoteId,
        orderId,
        isReleaseDfm,
        isReleaseDfmOdp,
        onSubmit: onSubmit,
        setIsReleasing,
        form,
        form: { getFieldDecorator, getFieldsError },
    } = props

    const client = useApolloClient()

    const identity = useSelector(getUserIdentity)

    const { data: quoteData } = useQuery(quoteDetailsForInviteAndMember, {
        variables: {
            id: quoteId,
        },
    })

    const quote = quoteData?.quote

    const currentUserEmail = identity?.email

    const [showCustamizableEmailSection, setShowCustamizableEmailSection] =
        useState<boolean>(false)

    const showEmailSection = (event: Event) => {
        event.preventDefault()
        setShowCustamizableEmailSection(true)
    }

    const isCollaborator = (email: string) =>
        quote?.members?.items?.map(
            (option: any) => option?.user?.email === email,
        )

    const emailValidator = () => {
        return async (_rule: any, values: string[], callback: Function) => {
            const enteredEmail = (values && values.slice(-1)[0]) || ''

            const checkCollaborator =
                isCollaborator(enteredEmail)?.includes(true)

            if (!values || checkCollaborator || !enteredEmail) {
                return
            }

            const {
                data: { isEmployeeEmail },
            } = await client.query({
                query: isEmployeeEmailQuery,
                variables: {
                    email: enteredEmail,
                },
            })

            if (!isEmployeeEmail) {
                callback(
                    'Please select a collaborator or use a valid @fictiv.com email address',
                )
            }
            return null
        }
    }

    const submit = (evt: Event | FormEvent) => {
        evt.preventDefault()

        getValidFunctionality({
            onClose,
            quoteId,
            orderId,
            isReleaseDfm,
            isReleaseDfmOdp,
            setIsReleasing,
            client,
            form,
            skipEmails: false,
            showCustamizableEmailSection,
        })

        if (typeof onSubmit === 'function') {
            onSubmit()
        }
    }

    const releaseQuoteWithoutEmail = (evt: Event | FormEvent) => {
        evt.preventDefault()

        getValidFunctionality({
            onClose,
            quoteId,
            orderId,
            isReleaseDfm,
            isReleaseDfmOdp,
            setIsReleasing,
            client,
            form,
            skipEmails: true,
            showCustamizableEmailSection: false,
        })

        if (typeof onSubmit === 'function') {
            onSubmit()
        }
    }

    const customizeEmailTag = () => {
        return (
            <Box style={{ padding: '0 0 20px 60px' }}>
                <EditOutlined
                    style={{
                        color: getColor('link'),
                        fontSize: '13px',
                        paddingRight: '10px',
                        paddingTop: '2px',
                    }}
                />
                <Link
                    style={{ fontWeight: 500 }}
                    onClick={showEmailSection}
                    name="customize-email"
                    placement="customize email"
                >
                    Customize email
                </Link>
            </Box>
        )
    }

    const customizeEmailForm = () => {
        return (
            <Form
                style={{
                    backgroundColor: '#FAFAFA',
                    borderTop: `1px solid ${getColor('blackAlpha15')}`,
                }}
                onSubmit={submit}
                hideRequiredMark
            >
                <Form.Item
                    style={{ ...customizeEmailFormStyles, marginTop: '24px' }}
                    {...layout}
                    label={
                        <span
                            style={{
                                ...customizeEmailFormTextStyles,
                            }}
                        >
                            From
                        </span>
                    }
                >
                    {getFieldDecorator(FROM, {
                        initialValue: currentUserEmail,
                    })(<Input data-test-target="email-from" disabled />)}
                </Form.Item>
                <Form.Item
                    style={{
                        ...customizeEmailFormStyles,
                    }}
                    {...layout}
                    label={
                        <span
                            style={{
                                ...customizeEmailFormTextStyles,
                            }}
                        >
                            To
                        </span>
                    }
                >
                    {getFieldDecorator(TO, {
                        initialValue: quote?.user?.email,
                        rules: [
                            {
                                required: true,
                                type: 'email',
                                message: 'Enter a valid email address!',
                            },
                        ],
                    })(<Input data-test-target="email-to" disabled />)}
                </Form.Item>
                <Form.Item
                    style={{
                        ...customizeEmailFormStyles,
                    }}
                    {...layout}
                    label={
                        <span style={{ ...customizeEmailFormTextStyles }}>
                            Cc
                        </span>
                    }
                >
                    {getFieldDecorator(CC, {
                        rules: [
                            {
                                required: false,
                            },
                            {
                                validator: emailValidator(),
                            },
                        ],
                    })(
                        <Select
                            mode="tags"
                            tokenSeparators={[',', ' ', '\n', '\t']}
                        >
                            {quote?.members?.items?.map((option: any) => (
                                <Option
                                    value={option?.user?.email}
                                    key={option?.user?.email}
                                >
                                    {option?.user?.email}
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
                <Form.Item
                    style={{
                        ...customizeEmailFormStyles,
                    }}
                    {...layout}
                    label={
                        <span
                            style={{
                                ...customizeEmailFormTextStyles,
                            }}
                        >
                            Subject
                        </span>
                    }
                >
                    {getFieldDecorator(SUBJECT, {
                        initialValue:
                            isReleaseDfm || isReleaseDfmOdp
                                ? 'Some DFM reports require your action'
                                : 'Your quote is ready to review!',
                        rules: [
                            {
                                required: true,
                                message: 'Required',
                            },
                        ],
                    })(<Input data-test-target="email-subject" />)}
                </Form.Item>
                <Box
                    direction="col"
                    style={{ paddingLeft: '24px', paddingTop: '24px' }}
                >
                    <Text
                        style={{
                            fontWeight: 500,
                            paddingBottom: '8px',
                        }}
                    >
                        Add notes for the customer to review with their quote.
                    </Text>
                    <Form.Item style={{ marginRight: '24px' }}>
                        {getFieldDecorator(NOTES, {
                            initialValue: '',
                        })(
                            <Input.TextArea
                                autoSize={{ minRows: 3, maxRows: 5 }}
                                maxLength={2000}
                                placeholder={EMAIL_SUBJECT_PLACEHOLDER}
                            />,
                        )}
                        <span style={{ paddingBottom: '5px' }}>
                            {' '}
                            2000 characters max
                        </span>
                    </Form.Item>
                </Box>
            </Form>
        )
    }

    const emailSection = () =>
        showCustamizableEmailSection
            ? customizeEmailForm()
            : customizeEmailTag()

    return (
        <Modal
            bodyStyle={{ backgroundColor: 'white', padding: '0px' }}
            visible
            footer={null}
            closable={false}
            width="520px"
            maskClosable
        >
            <Box direction="col">
                <Box
                    style={{
                        backgroundColor: 'white',
                        padding: '24px 24px 0 24px',
                    }}
                >
                    <ExclamationCircleOutlined
                        style={{
                            color: getColor('warning'),
                            fontSize: '21px',
                            marginRight: '18px',
                        }}
                    />
                    <Box direction="col" flex="1">
                        <Text
                            fontSize="16px"
                            fontWeight={400}
                            color="blackAlpha65"
                            style={{
                                marginBottom: '4px',
                                width: '90%',
                            }}
                        >
                            {isReleaseDfm || isReleaseDfmOdp
                                ? RELEASE_DFM_TITLE
                                : RELEASE_QUOTE_TITLE}
                        </Text>
                        <Text
                            color="blackAlpha65"
                            fontWeight={400}
                            style={{
                                marginBottom: '24px',
                            }}
                        >
                            {getValidDescription(isReleaseDfmOdp, isReleaseDfm)}
                        </Text>
                    </Box>
                </Box>
                {emailSection()}
                <Box
                    justifyContent="end"
                    padding="15px"
                    borderTop={`1px solid ${getColor('blackAlpha15')}`}
                >
                    <Button
                        onClick={releaseQuoteWithoutEmail}
                        name={`${RELEASE_DFM_CONFIRMATION}-confirm-without-email`}
                        placement={RELEASE_DFM_CONFIRMATION}
                        data-test-target={`${RELEASE_DFM_CONFIRMATION}-confirm-without-email`}
                        style={{
                            marginRight: '12px',
                            color: 'red',
                        }}
                    >
                        Release without email
                    </Button>
                    <Button
                        onClick={onClose}
                        name={`${RELEASE_DFM_CONFIRMATION}-cancel`}
                        placement={RELEASE_DFM_CONFIRMATION}
                        data-test-target={`${RELEASE_DFM_CONFIRMATION}-cancel`}
                        style={{
                            marginRight: '10px',
                            marginLeft: 'auto',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={submit}
                        disabled={
                            (!areAllRequiredFieldsFilled(
                                form,
                                REQUIRED_FIELDS,
                            ) ||
                                hasErrors(getFieldsError())) &&
                            showCustamizableEmailSection
                        }
                        name={`${RELEASE_DFM_CONFIRMATION}-confirm`}
                        placement={RELEASE_DFM_CONFIRMATION}
                        data-test-target={`${RELEASE_DFM_CONFIRMATION}-confirm`}
                    >
                        {isReleaseDfm || isReleaseDfmOdp
                            ? 'Release DFM'
                            : 'Release quote and DFM'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    )
}

export default Form.create<ConfirmationModalPropsType>()(
    ReleaseDFMConfirmationModal,
)
